import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const IMPORT_FIELDS = [
  { v: "ignore", label: "— Ignorer —" },
  { v: "last_name", label: "Nom" },
  { v: "first_name", label: "Prénom" },
  { v: "company_name", label: "Société / raison sociale" },
  { v: "email", label: "Email" },
  { v: "phone", label: "Téléphone" },
  { v: "address", label: "Adresse" },
  { v: "postal_code", label: "Code postal" },
  { v: "city", label: "Ville" },
  { v: "siret", label: "SIRET" },
  { v: "vat_number", label: "N° TVA" },
  { v: "status", label: "Statut (prospect/actif)" },
  { v: "tag", label: "Type (régulier/occasionnel)" },
  { v: "notes", label: "Notes" },
];

const HEADER_RULES = [
  ["first_name", ["prénom", "prenom", "first"]],
  ["company_name", ["société", "societe", "raison", "entreprise", "company"]],
  ["email", ["email", "e-mail", "mail", "courriel"]],
  ["phone", ["tél", "tel", "phone", "portable", "mobile"]],
  ["postal_code", ["code postal", "cp", "postal"]],
  ["address", ["adresse", "address", "rue"]],
  ["city", ["ville", "city", "commune"]],
  ["siret", ["siret", "siren"]],
  ["vat_number", ["tva", "vat"]],
  ["status", ["statut", "status"]],
  ["tag", ["fréquence", "frequence", "régulier", "regulier", "tag"]],
  ["notes", ["note", "commentaire", "remarque", "observation"]],
  ["last_name", ["nom", "name", "contact", "client"]],
];

export function guessClientsMapping(rows, hasHeader) {
  const mapping = {};
  const used = new Set();
  const nCols = Math.max(...rows.map((r) => r.length));
  if (hasHeader && rows[0]) {
    rows[0].forEach((h, i) => {
      const hl = (h || "").toLowerCase();
      if (!hl) return;
      for (const [field, kws] of HEADER_RULES) {
        if (used.has(field)) continue;
        if (kws.some((k) => hl.includes(k))) {
          mapping[i] = field;
          used.add(field);
          return;
        }
      }
    });
  }
  // Devine par contenu sur les colonnes non mappées
  const dataRows = rows.slice(hasHeader ? 1 : 0, (hasHeader ? 1 : 0) + 20);
  for (let i = 0; i < nCols; i++) {
    if (mapping[i]) continue;
    const vals = dataRows.map((r) => (r[i] || "").trim()).filter(Boolean);
    if (!vals.length) continue;
    const frac = (fn) => vals.filter(fn).length / vals.length;
    if (!used.has("email") && frac((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) > 0.6) {
      mapping[i] = "email"; used.add("email"); continue;
    }
    if (!used.has("phone") && frac((v) => /^(\+33|0)\d{9,10}$/.test(v.replace(/[\s.\-()]/g, ""))) > 0.6) {
      mapping[i] = "phone"; used.add("phone"); continue;
    }
    if (!used.has("siret") && frac((v) => /^\d{14}$/.test(v.replace(/\s/g, ""))) > 0.6) {
      mapping[i] = "siret"; used.add("siret"); continue;
    }
    if (!used.has("postal_code") && frac((v) => /^\d{5}$/.test(v)) > 0.6) {
      mapping[i] = "postal_code"; used.add("postal_code"); continue;
    }
  }
  if (!used.has("last_name") && !used.has("company_name")) {
    for (let i = 0; i < nCols; i++) {
      if (!mapping[i]) { mapping[i] = "last_name"; used.add("last_name"); break; }
    }
  }
  return mapping;
}

export default function ClientsImportWizard({ data, onClose, onDone }) {
  const [hasHeader, setHasHeader] = useState(data.hasHeader);
  const [mapping, setMapping] = useState(() => guessClientsMapping(data.rows, data.hasHeader));
  const [busy, setBusy] = useState(false);
  const [importErrors, setImportErrors] = useState(null);
  const nCols = Math.max(...data.rows.map((r) => r.length));
  const preview = data.rows.slice(hasHeader ? 1 : 0, (hasHeader ? 1 : 0) + 5);
  const totalRows = data.rows.length - (hasHeader ? 1 : 0);

  const mapped = Object.values(mapping);
  const canImport = mapped.includes("last_name") || mapped.includes("company_name") || mapped.includes("email");
  const [advanced, setAdvanced] = useState(!canImport);

  const setCol = (i, v) => {
    const next = { ...mapping };
    if (v === "ignore") delete next[i];
    else {
      Object.keys(next).forEach((k) => { if (next[k] === v) delete next[k]; });
      next[i] = v;
    }
    setMapping(next);
  };

  const fieldLabel = (v) => IMPORT_FIELDS.find((f) => f.v === v)?.label || v;
  const colsFor = (field) => Object.entries(mapping).filter(([, v]) => v === field).map(([k]) => +k);
  const colName = (i) => (hasHeader && data.rows[0]?.[i]) ? `« ${data.rows[0][i]} »` : `colonne ${i + 1}`;
  const detected = IMPORT_FIELDS.filter((f) => f.v !== "ignore")
    .map((f) => ({ f: f.v, cols: colsFor(f.v) })).filter((d) => d.cols.length);
  const simplePreview = preview.slice(0, 3).map((r) => {
    const get = (f) => colsFor(f).map((i) => r[i]).filter(Boolean).join(" ");
    const name = [get("first_name"), get("last_name")].filter(Boolean).join(" ") || get("company_name");
    return { name, email: get("email"), city: get("city"), phone: get("phone") };
  });

  const doImport = async () => {
    setBusy(true);
    setImportErrors(null);
    try {
      const rows = data.rows.slice(hasHeader ? 1 : 0);
      const { data: res } = await api.post("/clients/import-mapped", { rows, mapping });
      if (res.errors?.length) {
        setImportErrors({ created: res.created, skipped: res.skipped_duplicates, list: res.errors });
        setAdvanced(true);
        if (res.created > 0) toast.success(`${res.created} client(s) créé(s) — voir les lignes en erreur ci-dessous`);
      } else {
        toast.success(`Import : ${res.created} client(s) créé(s), ${res.skipped_duplicates} doublon(s) ignoré(s)`);
        onDone();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} data-testid="clients-import-wizard">
      <div className="card w-full max-w-5xl max-h-[90vh] overflow-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl" style={{ fontWeight: 700 }}>Importer des clients</h2>
            <p className="text-sm text-secondary-text mt-1">
              {advanced
                ? <>{totalRows} ligne{totalRows > 1 ? "s" : ""} détectée{totalRows > 1 ? "s" : ""}. Indiquez à quoi correspond chaque colonne — au minimum un <b>Nom</b>, une <b>Société</b> ou un <b>Email</b>.</>
                : <>{totalRows} client{totalRows > 1 ? "s" : ""} prêt{totalRows > 1 ? "s" : ""} à importer — rien à configurer.</>}
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="clients-import-close"><X size={16} /></button>
        </div>

        {!advanced ? (
          <div data-testid="clients-import-simple-view">
            <div className="rounded-lg p-4 mb-4 flex items-start gap-3"
                 style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.35)" }}>
              <div className="text-xl" style={{ color: "#22c55e" }}>✓</div>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#22c55e" }}>Fichier reconnu automatiquement</div>
                <div className="text-xs mt-1.5 flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--chalk)" }}>
                  {detected.map((d) => (
                    <span key={d.f}><b style={{ color: "#FFC400" }}>{fieldLabel(d.f)}</b> ← {d.cols.map(colName).join(" + ")}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="border rounded-lg divide-y" style={{ borderColor: "#3A3D42" }}>
              {simplePreview.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5" style={{ borderColor: "#3A3D42" }}>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{c.name || "—"}</div>
                    <div className="text-[11px]" style={{ color: "var(--grey)" }}>{[c.email, c.phone].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <div className="text-xs whitespace-nowrap" style={{ color: "var(--grey)" }}>{c.city}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] mt-2" style={{ color: "var(--grey)" }}>
              Aperçu des 3 premiers clients — {totalRows} seront importés. Les doublons (même email ou même nom+prénom) sont ignorés.
            </div>
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
              <input type="checkbox" checked={hasHeader} onChange={(e) => { setHasHeader(e.target.checked); setMapping(guessClientsMapping(data.rows, e.target.checked)); }} data-testid="clients-import-has-header" />
              La première ligne contient des titres (pas un client)
            </label>
            <div className="overflow-x-auto border rounded-lg" style={{ borderColor: "#3A3D42" }}>
              <table className="table" style={{ minWidth: nCols * 160 }}>
                <thead>
                  <tr>
                    {Array.from({ length: nCols }, (_, i) => (
                      <th key={i} className="p-2">
                        <select value={mapping[i] || "ignore"} onChange={(e) => setCol(i, e.target.value)}
                          className="input text-xs py-1.5 w-full" data-testid={`clients-col-map-${i}`}
                          style={mapping[i] ? { borderColor: "#FFC400", color: "#FFC400" } : {}}>
                          {IMPORT_FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                        </select>
                        {hasHeader && <div className="text-[10px] mt-1 text-[var(--grey)] truncate font-normal">{data.rows[0]?.[i]}</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, ri) => (
                    <tr key={ri}>
                      {Array.from({ length: nCols }, (_, ci) => (
                        <td key={ci} className="text-xs max-w-[220px] truncate" title={r[ci] || ""}>{r[ci] || <span className="text-[var(--grey)]">—</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalRows > 5 && <div className="text-[11px] text-[var(--grey)] mt-2">Aperçu des 5 premières lignes — {totalRows} seront importées.</div>}
          </>
        )}

        {importErrors && (
          <div className="rounded-lg p-4 mt-4" data-testid="clients-import-errors-box"
               style={{ background: "rgba(225,6,0,0.08)", border: "1px solid rgba(225,6,0,0.4)" }}>
            <div className="text-sm font-semibold mb-2" style={{ color: "#FF6B66" }}>
              {importErrors.created} créé(s) · {importErrors.skipped} doublon(s) · {importErrors.list.length} ligne(s) en erreur :
            </div>
            <div className="max-h-40 overflow-auto space-y-1">
              {importErrors.list.map((e, i) => (
                <div key={i} className="text-xs" style={{ color: "var(--chalk)" }}>Ligne {e.row} : {e.error}</div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-5">
          {advanced ? (
            <div className="text-xs" style={{ color: canImport ? "#22c55e" : "#E10600" }}>
              {canImport ? "✓ Prêt à importer" : "Associez au moins Nom, Société ou Email"}
            </div>
          ) : (
            <button onClick={() => setAdvanced(true)} className="btn btn-ghost btn-sm text-xs" data-testid="clients-import-show-advanced">
              Ajuster les colonnes (avancé)
            </button>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-outline">Annuler</button>
            <button onClick={doImport} disabled={!canImport || busy} className="btn btn-accent" data-testid="clients-import-submit" style={!canImport || busy ? { opacity: 0.4, cursor: "not-allowed" } : {}}>
              {busy ? "Import en cours..." : `Importer ${totalRows} client${totalRows > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
